import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Shield, Lock, Eye, Trash2 } from 'lucide-react'
import SEO, { schemas } from '../components/SEO'
import { useLocale } from '../i18n/Link'

const PRIVACY_EMAIL = 'privacy@cmdesk.help'
const UPDATED_AT = new Date(2026, 2, 28)

const sectionIcons = [Shield, Eye, Lock, Trash2]

interface Section {
  title: string
  content: string[]
}

export default function Privacy() {
  const { t } = useTranslation(['privacy', 'common'])
  const { htmlLang } = useLocale()
  const updatedAt = UPDATED_AT.toLocaleDateString(htmlLang, { day: 'numeric', month: 'long', year: 'numeric' })
  const sections = (t('sections', { returnObjects: true, email: PRIVACY_EMAIL }) as Section[]).map((section, i) => ({
    ...section,
    icon: sectionIcons[i],
  }))

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/privacy"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.privacy'), url: '/privacy' }])}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            {t('hero.titleStart')}{' '}
            <span className="text-gradient">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg">
            {t('hero.updated', { date: updatedAt })}
          </p>
        </motion.div>

        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-white">{section.title}</h2>
              </div>
              <div className="text-gray-400 leading-relaxed whitespace-pre-line">
                {section.content.join('\n')}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 glass rounded-2xl p-8 text-center"
        >
          <h2 className="text-xl font-bold text-white mb-4">{t('contact.title')}</h2>
          <p className="text-gray-400 mb-6">
            {t('contact.text')}
          </p>
          <a
            href={`mailto:${PRIVACY_EMAIL}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-colors"
          >
            {PRIVACY_EMAIL}
          </a>
        </motion.div>
      </div>
    </motion.div>
  )
}
